import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useLocaleSync } from '@/hooks/useUiStrings'
import { useStore } from '@/store/useStore'
import { hasAccessToken } from '@/services/session'
import { hydrateSessionProfile } from '@/services/refreshSession'
import AppLayout from '@/layouts/AppLayout'
import AppReloadBanner from '@/components/AppReloadBanner'
import FeedbackControl from '@/components/FeedbackControl'
import MeetingInviteBanner from '@/components/MeetingInviteBanner'
import ShareOnCreateErrorNotice from '@/components/ShareOnCreateErrorNotice'
import { PROJECT_MANAGEMENT_ENABLED } from '@/constants/feature-flags'
import LoginPage from '@/pages/Login'
import RegisterPage from '@/pages/Register'
import AuthCallbackPage from '@/pages/AuthCallback'
import HomePage from '@/pages/Home'
import HistoryPage from '@/pages/History'
import UpcomingPage from '@/pages/Upcoming'
import GlossaryPage from '@/pages/Glossary'
import ProjectsPage from '@/pages/Projects'
import ProjectDetailPage from '@/pages/Projects/ProjectDetailPage'
import SettingsPage from '@/pages/Settings'
import MeetingsPage from '@/pages/Meetings'
import InternalMeetingRecordPage from '@/pages/Meetings/InternalMeetingRecordPage'
import MeetingDetailPage from '@/pages/Meetings/MeetingDetailPage'
import AdminPage from '@/pages/Admin'

const TeamsMeetingPanel = lazy(() => import('@/pages/TeamsMeetingPanel'))
const TeamsMeetingConfig = lazy(() => import('@/pages/TeamsMeetingConfig'))

function TeamsPageFallback() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-[var(--bg-primary)]">
      <span className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--border-color)] border-t-[var(--accent)]" />
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  useLocaleSync()
  const isAuthenticated = useStore((s) => s.isAuthenticated)
  const user = useStore((s) => s.user)
  const logout = useStore((s) => s.logout)
  const hasToken = hasAccessToken()

  useEffect(() => {
    if (!hasToken && isAuthenticated) logout()
  }, [hasToken, isAuthenticated, logout])

  // Sessions stored before authProvider existed have no way to say which
  // calendar they belong to; a refresh fills it in without a re-login.
  const needsProfileHydration = hasToken && Boolean(user) && !user?.authProvider
  useEffect(() => {
    if (!needsProfileHydration) return
    void hydrateSessionProfile()
  }, [needsProfileHydration])

  if (!hasToken) return <Navigate to="/login" replace />
  return (
    <>
      <AppReloadBanner />
      {children}
    </>
  )
}

/**
 * Route guard that only admits users with the `admin` role, redirecting
 * everyone else to the home page. Assumes it is nested under `ProtectedRoute`.
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const role = useStore((s) => s.user?.role)
  if (role !== 'admin') return <Navigate to="/home" replace />
  return <>{children}</>
}

const LIVE_MEETING_PATH = /^\/meetings\/[^/]+\/live\/?$/

export function AppRoutes() {
  const location = useLocation()
  const hasToken = hasAccessToken()
  const isAuthenticated = useStore((s) => s.isAuthenticated)
  const isTeamsEmbeddedRoute = location.pathname.startsWith('/teams/')
  const isLiveMeetingRoute = LIVE_MEETING_PATH.test(location.pathname)

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route
          path="/teams/meeting"
          element={
            <Suspense fallback={<TeamsPageFallback />}>
              <TeamsMeetingPanel />
            </Suspense>
          }
        />
        <Route
          path="/teams/config"
          element={
            <Suspense fallback={<TeamsPageFallback />}>
              <TeamsMeetingConfig />
            </Suspense>
          }
        />
        <Route
          element={
            <ProtectedRoute>
              <Outlet />
            </ProtectedRoute>
          }
        >
          <Route element={<AppLayout />}>
            <Route path="/home" element={<HomePage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/upcoming" element={<UpcomingPage />} />
            <Route path="/glossary" element={<GlossaryPage />} />
            <Route
              path="/projects"
              element={
                PROJECT_MANAGEMENT_ENABLED ? <ProjectsPage /> : <Navigate to="/home" replace />
              }
            />
            <Route
              path="/projects/:projectKey"
              element={
                PROJECT_MANAGEMENT_ENABLED ? (
                  <ProjectDetailPage />
                ) : (
                  <Navigate to="/home" replace />
                )
              }
            />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/meetings/:id" element={<MeetingDetailPage />} />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              }
            />
          </Route>
          <Route path="/meetings/:id/live" element={<MeetingsPage />} />
          <Route path="/meetings/:id/record" element={<InternalMeetingRecordPage />} />
        </Route>
        <Route path="/" element={<Navigate to={hasToken ? '/home' : '/login'} replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {hasToken && isAuthenticated && !isTeamsEmbeddedRoute && !isLiveMeetingRoute && (
        <FeedbackControl />
      )}
      {/* Outside <Routes> so an invite that lands mid-meeting is not unmounted
          by the navigation it offers. Teams panels are excluded for the same
          reason FeedbackControl is: they render inside a host chrome. Live
          meeting hides FeedbackControl so the Copilot FAB owns bottom-right. */}
      {hasToken && isAuthenticated && !isTeamsEmbeddedRoute && <MeetingInviteBanner />}
      {hasToken && isAuthenticated && !isTeamsEmbeddedRoute && <ShareOnCreateErrorNotice />}
    </>
  )
}
